import { withAITracking } from '@microsoft/applicationinsights-react-js';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import { useTheme } from '@mui/material/styles';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useProductCenterApi } from 'api';
import { doNotRefetch } from 'api/ReactQueryOptions';
import { releaseNotesListQuery } from 'api/queries';
import { AreaName } from 'domain/constants';
import { isExternalUser } from 'domain/user/User';
import { AccordionContainer } from 'library/components/controls/accordion/AccordionContainer';
import { fromIsoToDate } from 'library/formatters/DateFormatter';
import { OperationType } from 'library/models/enums/OperationType';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';
import { checkUserAccess } from 'main/auth/checkUserAccess';
import { reactPlugin } from 'main/azureInsights/config';
import TitleWrapper from 'main/layout/TitleWrapper';
import { home_HomeUrl } from 'main/routes/urls/RouteUrls';
import { ReleaseNoteAccordion } from 'modules/ReleaseNotes/ReleaseNoteAccordion';
import { ReleaseNoteDevAccordion } from 'modules/ReleaseNotes/ReleaseNoteDevAccordion';
import { usePagination } from 'modules/ReleaseNotes/usePagination';

const ReleaseNotesPage = () => {
	const user = useAppSelector(selectUser);
	const { api } = useProductCenterApi();
	const { data } = useSuspenseQuery({
		...releaseNotesListQuery(api),
		retry: 1,
		...doNotRefetch,
	});
	const { spacing, typography } = useTheme();
	const { t } = useTranslation(['commonButton', 'home']);
	const publishedReleaseNotes = useMemo(
		() =>
			data
				.filter(({ isPublished }) => isPublished)
				.sort(
					({ date: dateA }, { date: dateB }) =>
						fromIsoToDate(dateB).getTime() -
						fromIsoToDate(dateA).getTime()
				),
		[data]
	);
	const { count, handleChange, page, releaseNotesPage } = usePagination(
		publishedReleaseNotes
	);

	const queryClient = useQueryClient();
	const isPcDevAccess = useMemo(
		() =>
			checkUserAccess(queryClient, {
				areaName: AreaName.DevelopersTestPage,
				permission: OperationType.Read,
			}),
		[]
	);

	return (
		<>
			<TitleWrapper
				category={t('home:root-title')}
				title={t('home:release-notes')}
			/>
			<AccordionContainer
				defaultExpanded={publishedReleaseNotes[0]?.id.toString()}>
				{releaseNotesPage.map((releaseNote) => (
					<>
						{isPcDevAccess ? (
							<ReleaseNoteDevAccordion
								key={releaseNote.id}
								releaseNote={releaseNote}
							/>
						) : (
							<ReleaseNoteAccordion
								isExternal={isExternalUser(user)}
								key={releaseNote.id}
								releaseNote={releaseNote}
							/>
						)}
					</>
				))}
			</AccordionContainer>
			<Box display="flex" justifyContent="center" marginTop={spacing(2)}>
				<Pagination count={count} page={page} onChange={handleChange} />
			</Box>
			<Box display="flex" justifyContent="end" marginTop={spacing(2)}>
				<Button
					variant="text"
					to={home_HomeUrl}
					component={Link}
					endIcon={<ArrowForward />}
					sx={{
						fontSize: typography.h6.fontSize,
					}}>
					{t('commonButton:back')}
				</Button>
			</Box>
		</>
	);
};

export default withAITracking(
	reactPlugin,
	ReleaseNotesPage,
	'ReleaseNotesPage',
	'application-insight-container'
);
